/**
 * @fileoverview Unified Photos Grid Component
 *
 * Displays all project photos in a unified grid view.
 * Photos filtered by selected project parts (no distinction between general/part photos).
 *
 * @module RenovationProject/CustomSections/UnifiedPhotosGrid
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { FileLog } from 'shared/types/file';
import { useProjectParts } from '../contexts/ProjectPartsContext';
import { Image as ImageIcon } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface UnifiedPhotosGridProps {
  projectId?: number;
  selectedPartIds: number[];
}

/** Pseudo-onderdeel voor foto's zonder projectonderdeel — zie PhotosModal */
const NO_PROJECT_PART_ID = -1;

export const UnifiedPhotosGrid: React.FC<UnifiedPhotosGridProps> = ({
  projectId: propProjectId,
  selectedPartIds,
}) => {
  const { id } = useParams<{ id: string }>();
  const { session } = useSession();
  const projectId = propProjectId || parseInt(id || '0', 10);

  const { parts: projectParts, isLoading: isLoadingParts } = useProjectParts();

  const [allPhotos, setAllPhotos] = useState<FileLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<FileLog | null>(null);

  // Fetch all photos (general + all project parts)
  useEffect(() => {
    const fetchAllPhotos = async () => {
      if (!session || !projectId) {
        console.log('[UnifiedPhotosGrid] Waiting for session or projectId');
        setIsLoading(false);
        return;
      }

      // Wait for parts to load before fetching photos
      if (isLoadingParts) {
        console.log('[UnifiedPhotosGrid] Waiting for project parts to load');
        return;
      }

      console.log('[UnifiedPhotosGrid] Fetching photos for project', projectId);
      setIsLoading(true);
      try {
        const photoPromises: Promise<FileLog[]>[] = [];

        // Fetch general project photos
        const generalPromise = fetchWithAuth(
          `${API_URL}/file/renovation-project/${projectId}/images`,
          { method: 'GET' },
          session
        ).then((res) => {
          if (res.ok) return res.json();
          return [];
        });
        photoPromises.push(generalPromise);

        // Fetch photos for each project part
        projectParts.forEach((part) => {
          const partPromise = fetchWithAuth(
            `${API_URL}/file/project-part/${part.id}/images`,
            { method: 'GET' },
            session
          ).then((res) => {
            if (res.ok) return res.json();
            return [];
          });
          photoPromises.push(partPromise);
        });

        const results = await Promise.all(photoPromises);
        const combinedPhotos = results.flat();
        console.log('[UnifiedPhotosGrid] Fetched total photos:', combinedPhotos.length);

        // Sort by documentDate (newest first)
        combinedPhotos.sort((a, b) => {
          const dateA = new Date(a.documentDate || a.createdAt || 0).getTime();
          const dateB = new Date(b.documentDate || b.createdAt || 0).getTime();
          return dateB - dateA;
        });

        setAllPhotos(combinedPhotos);
      } catch (error) {
        console.error('[UnifiedPhotosGrid] Error fetching photos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllPhotos();
  }, [session, projectId, projectParts, isLoadingParts]);

  // Filter photos by selected parts
  const filteredPhotos = useMemo(() => {
    if (selectedPartIds.length === 0) return [];
    return allPhotos.filter((photo) => {
      const partId = (photo as any).projectPartId;
      // Foto's zonder projectonderdeel volgen het pseudo-onderdeel dat
      // PhotosModal meegeeft (NO_PROJECT_PART_ID = -1)
      if (partId == null) return selectedPartIds.includes(NO_PROJECT_PART_ID);
      return selectedPartIds.includes(partId);
    });
  }, [allPhotos, selectedPartIds]);

  const getPhotoUrl = (photo: FileLog) => {
    if (photo.imageUrl) return photo.imageUrl;
    if (photo.sharePointFileUrl) return photo.sharePointFileUrl;
    return photo.s3Url || '/placeholder-image.jpg';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Geen datum';
    try {
      return format(new Date(dateString), 'd MMM yyyy', { locale: nl });
    } catch {
      return 'Ongeldige datum';
    }
  };

  if (isLoading || isLoadingParts) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-moss-primary" />
      </div>
    );
  }

  if (filteredPhotos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-neutral-200 bg-white py-12 text-center">
        <ImageIcon size={48} weight="thin" className="mb-3 text-neutral-400" />
        <p className="text-neutral-600">Geen foto's beschikbaar</p>
        <p className="text-sm text-neutral-400">
          {selectedPartIds.length === 0
            ? 'Selecteer projectonderdelen om foto\'s te zien'
            : 'Nog geen foto\'s geüpload voor de geselecteerde onderdelen'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-6 gap-3">
        {filteredPhotos.map((photo) => (
          <div
            key={photo.id}
            className="group relative aspect-square cursor-pointer overflow-hidden rounded-md bg-neutral-100 transition-transform hover:scale-[1.02]"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={getPhotoUrl(photo)}
              alt={Array.isArray(photo.label) ? photo.label.join(', ') : (photo.label || 'Project photo')}
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100">
              <div className="absolute right-0 bottom-0 left-0 p-2">
                <p className="truncate text-xs font-medium text-white">
                  {formatDate(photo.documentDate as string)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="relative max-h-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getPhotoUrl(selectedPhoto)}
              alt={Array.isArray(selectedPhoto.label) ? selectedPhoto.label.join(', ') : (selectedPhoto.label || 'Project photo')}
              className="max-h-[90vh] max-w-full rounded-lg object-contain"
            />
            <div className="absolute top-4 right-4 max-w-xs rounded-lg bg-white p-4 shadow-xl">
              <p className="mb-2 text-sm font-semibold text-neutral-primary">
                {formatDate(selectedPhoto.documentDate as string)}
              </p>
              {selectedPhoto.label && (
                <p className="mb-1 text-sm text-neutral-secondary-mid">
                  {selectedPhoto.label}
                </p>
              )}
              {selectedPhoto.summary && (
                <p className="mt-2 text-xs text-neutral-secondary-mid">
                  {selectedPhoto.summary}
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-full bg-white text-neutral-primary transition-colors hover:bg-neutral-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};
